import { supabase } from '../lib/supabase';

const TABLE_NAME = 'audit_log';

const DEFAULT_PAGE_SIZE = 25;

function enrichWithUser(entry) {
  let userEmail = null;
  if (entry.details) {
    try {
      const details = typeof entry.details === 'string' ? JSON.parse(entry.details) : entry.details;
      userEmail = details.user_email || null;
    } catch {
      // ignore
    }
  }
  return {
    ...entry,
    user: entry.created_by ? { id: entry.created_by, email: userEmail || 'Unknown User' } : null,
  };
}

/**
 * Get audit log entries with optional filters and pagination (server-side).
 * @param {Object} options
 * @param {string} options.search - Free-text search (matches action, entity_type, entity_id, details text)
 * @param {string} options.dateFrom - ISO date string for start of range (inclusive)
 * @param {string} options.dateTo - ISO date string for end of range (inclusive)
 * @param {string} options.entityType - Filter by entity_type
 * @param {string} options.action - Filter by action
 * @param {string} options.actorId - Filter by created_by (actor user id)
 * @param {number} options.page - 1-based page number
 * @param {number} options.limit - Page size
 * @param {boolean} options.asc - Sort ascending (default false = newest first)
 * @returns {Promise<{ data: Array, total: number }>}
 */
export const getAuditLogFiltered = async (options = {}) => {
  const {
    search,
    dateFrom,
    dateTo,
    entityType,
    action,
    actorId,
    page = 1,
    limit = DEFAULT_PAGE_SIZE,
    asc = false,
  } = options;

  try {
    let query = supabase
      .from(TABLE_NAME)
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: !!asc });

    if (search && search.trim()) {
      const term = `%${search.trim()}%`;
      query = query.or(`action.ilike.${term},entity_type.ilike.${term},details->>user_email.ilike.${term}`);
    }
    if (dateFrom) query = query.gte('created_at', dateFrom);
    if (dateTo) {
      const end = new Date(dateTo);
      end.setHours(23, 59, 59, 999);
      query = query.lte('created_at', end.toISOString());
    }
    if (entityType) query = query.eq('entity_type', entityType);
    if (action) query = query.eq('action', action);
    if (actorId) query = query.eq('created_by', actorId);

    const from = (page - 1) * limit;
    const to = from + limit - 1;
    const { data, error, count } = await query.range(from, to);

    if (error) throw error;

    const entries = (data || []).map(enrichWithUser);
    return { data: entries, total: count ?? entries.length };
  } catch (error) {
    console.error('Error fetching audit log:', error);
    return { data: [], total: 0 };
  }
};

// Get all audit log entries (legacy: no filters, simple limit; newest first)
export const getAuditLog = async (limit = 100) => {
  const { data } = await getAuditLogFiltered({ limit, page: 1 });
  return data;
};

// Create an audit log entry
export const createAuditLog = async (action, entityType, entityId, details = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    // Add user email to details for easier retrieval
    const detailsWithUser = {
      ...details,
      user_email: user?.email || null,
    };
    
    const auditEntry = {
      action,
      entity_type: entityType,
      entity_id: entityId,
      details: JSON.stringify(detailsWithUser),
      created_by: user?.id || null,
    };

    const { data, error } = await supabase
      .from(TABLE_NAME)
      .insert([auditEntry])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating audit log:', error);
    // Don't throw - audit logging should not break the main functionality
  }
};
